import { Button } from "@legal-500/legal500-design-system";
import { CopyableList } from "./CopyableList.js";
import { downloadResultsCsv } from "./moveToSetCsv.js";
import type {
  MoveToSetProgress,
  MoveToSetRunKind,
  MoveToSetRunResult,
  MoveToSetRunTotals,
} from "./useMoveToSetBatch.js";
import styles from "../PipelineBrowser.module.css";

type MoveToSetRunReportProps = {
  totals: MoveToSetRunTotals;
  skippedCount: number;
  results: MoveToSetRunResult[];
  progress: MoveToSetProgress;
  dryRun: boolean;
  stopped: boolean;
  blocked: boolean;
  runKind: MoveToSetRunKind;
  canRetry: boolean;
  retryDescribedBy?: string;
  onRetryFailed: () => void;
};

export function MoveToSetRunReport({
  totals,
  skippedCount,
  results,
  progress,
  dryRun,
  stopped,
  blocked,
  runKind,
  canRetry,
  retryDescribedBy,
  onRetryFailed,
}: MoveToSetRunReportProps) {
  const requested = results.filter(result => result.rowKind === "requested");
  const notFound = requested
    .filter(result => result.outcome === "not_found")
    .map(result => result.id);
  const failed = requested
    .filter(result => result.outcome === "failed")
    .map(result => (result.error ? `${result.id}\t${result.error}` : result.id));
  const unknown = requested.filter(result => result.outcome === "unknown").map(result => result.id);
  const notSent = requested
    .filter(result => result.outcome === "not_sent")
    .map(result => (result.error ? `${result.id}\t${result.error}` : result.id));
  const counters = [
    { label: dryRun ? "Would move" : "Moved", value: totals.moved },
    { label: "Unchanged (already on the set)", value: totals.unchanged },
    { label: "Not found", value: totals.notFound },
    {
      label:
        "Failed (not moved in the pipeline, approval-api may already show the change, re-running is safe)",
      value: totals.failed,
    },
    ...(totals.unknown > 0
      ? [{ label: "Unknown (re-run to confirm)", value: totals.unknown }]
      : []),
    ...(totals.notSent > 0
      ? [{ label: "Not sent (stopped by a family conflict)", value: totals.notSent }]
      : []),
    {
      label: dryRun
        ? "Family-mates that would move (not requested)"
        : "Family-mates moved (not requested)",
      value: totals.familyMatesMoved,
    },
    { label: "Skipped in the file", value: skippedCount },
  ];
  const unsent = progress.totalRows - progress.processedRows - progress.unknownRows;

  return (
    <div className={styles.syncStatePanel}>
      <div className={styles.contentSectionTitle}>
        Result: {dryRun ? "dry run (nothing written)" : "applied"}
        {stopped ? ", stopped by user" : ""}
        {blocked ? ", stopped by a family conflict" : ""}
      </div>
      <div className={styles.syncStateMessage}>
        {runKind === "retry" ? "Retry of failed rows: processed" : "Processed"}{" "}
        {progress.processedRows} / {progress.totalRows} rows in {progress.completedBatches} /{" "}
        {progress.totalBatches} batches.
        {progress.unknownRows > 0
          ? ` ${progress.unknownRows} rows were sent but their state is unknown.`
          : ""}
        {unsent > 0 ? ` ${unsent} rows were not processed.` : ""}
      </div>
      <table className={styles.reportTable}>
        <tbody>
          {counters.map(counter => (
            <tr key={counter.label}>
              <td>{counter.label}</td>
              <td>{counter.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {notFound.length > 0 && <CopyableList label="Not found ids" lines={notFound} />}
      {failed.length > 0 && <CopyableList label="Failed ids" lines={failed} />}
      {unknown.length > 0 && <CopyableList label="Unknown ids" lines={unknown} />}
      {notSent.length > 0 && <CopyableList label="Not sent ids" lines={notSent} />}
      <div className={styles.syncStateRow}>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          disabled={results.length === 0}
          onClick={() => downloadResultsCsv(results, dryRun, new Date())}
        >
          Download results CSV ({results.length} rows)
        </Button>
        {!dryRun && totals.failed > 0 && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            disabled={!canRetry}
            aria-describedby={retryDescribedBy}
            onClick={onRetryFailed}
          >
            Retry failed rows ({totals.failed})
          </Button>
        )}
      </div>
    </div>
  );
}
